import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, CheckCircle2, Clock, XCircle, AlertCircle, Crown } from 'lucide-react';

import API from '../../constants/api'
// API base via getApiBase()

const statusInfo = {
  non_demande: { label: 'Non demandé', color: 'bg-slate-100 text-slate-800', icon: AlertCircle },
  en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  suspendu: { label: 'Suspendu', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const benefits = [
  'Badge Premium sur votre profil',
  'Mise en avant dans l\'annuaire',
  'Accès aux demandes en priorité',
  'Statistiques avancées',
  'Support prioritaire'
];

export default function MySubscription() {
  const { user, token, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubscription = async () => {
    setRequesting(true);
    try {
      await axios.post(`${API}/subscription/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande d\'abonnement envoyée');
      fetchSubscription();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setRequesting(false);
    }
  };

  const StatusIcon = statusInfo[user?.subscription_status]?.icon || AlertCircle;

  return (
    <DashboardLayout>
      <div className="w-full">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Mon abonnement</h1>
        <p className="text-slate-600 mb-8">Gérez votre abonnement Premium</p>

        {/* Status Card */}
        <div className="lux-dashboard-card p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              user?.subscription_status === 'actif' ? 'bg-green-100' :
              user?.subscription_status === 'en_attente' ? 'bg-yellow-100' :
              user?.subscription_status === 'suspendu' ? 'bg-orange-100' :
              user?.subscription_status === 'refuse' ? 'bg-red-100' : 'bg-slate-100'
            }`}>
              <StatusIcon className={`w-6 h-6 ${
                user?.subscription_status === 'actif' ? 'text-green-600' :
                user?.subscription_status === 'en_attente' ? 'text-yellow-600' :
                user?.subscription_status === 'suspendu' ? 'text-orange-600' :
                user?.subscription_status === 'refuse' ? 'text-red-600' : 'text-slate-400'
              }`} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Statut de votre abonnement</h3>
              <Badge className={statusInfo[user?.subscription_status]?.color}>
                {statusInfo[user?.subscription_status]?.label}
              </Badge>
            </div>
          </div>

          {subscription?.activated_at && (
            <div className="mt-4 text-sm text-slate-600">
              Activé le {new Date(subscription.activated_at).toLocaleDateString('fr-FR')}
            </div>
          )}
        </div>

        {/* Benefits Card */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Crown className="w-6 h-6 text-amber-600" />
            <h2 className="font-semibold text-slate-900">Avantages Premium</h2>
          </div>
          <ul className="space-y-2">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-amber-600" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* Action */}
        {user?.subscription_status === 'non_demande' && (
          <div className="lux-dashboard-card p-6 text-center">
            <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Passez à Premium</h3>
            <p className="text-slate-600 mb-4">
              Demandez l'activation de votre abonnement pour bénéficier de tous les avantages Premium.
            </p>
            <p className="text-sm text-slate-500 mb-4">
              L'activation est effectuée par notre équipe après validation.
            </p>
            <Button onClick={handleRequestSubscription} disabled={requesting} data-testid="request-subscription">
              {requesting ? 'Envoi...' : 'Demander l\'abonnement'}
            </Button>
          </div>
        )}

        {user?.subscription_status === 'en_attente' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <Clock className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Demande en cours</h3>
            <p className="text-slate-600">
              Votre demande d'abonnement est en cours de traitement. Vous serez notifié dès son activation.
            </p>
          </div>
        )}

        {user?.subscription_status === 'actif' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Abonnement actif</h3>
            <p className="text-slate-600">
              Vous bénéficiez de tous les avantages Premium. Merci pour votre confiance !
            </p>
          </div>
        )}

        {user?.subscription_status === 'suspendu' && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-orange-600 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Abonnement suspendu</h3>
            <p className="text-slate-600">
              Votre abonnement a été temporairement suspendu. Contactez le support pour plus d'informations.
            </p>
          </div>
        )}

        {user?.subscription_status === 'refuse' && (
          <div className="lux-dashboard-card p-6 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Demande refusée</h3>
            <p className="text-slate-600 mb-4">
              Votre demande d'abonnement a été refusée. Vous pouvez faire une nouvelle demande.
            </p>
            <Button onClick={handleRequestSubscription} disabled={requesting}>
              Faire une nouvelle demande
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
